import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/lib/auth";
import KickedOutDialog from "@/components/auth/KickedOutDialog";
import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
// Iter-302 — Security helpers now live in AuthEngine.jsx (single source
// of truth). The legacy `@/lib/permissions` module re-exports the same
// helpers so pre-Iter-302 imports keep working, but new code must
// import from AuthEngine.
import {
  isOwner,
  hasAnyBrandPermission,
  isPersonalViewOnly,
  canAccessLaunchReset,
  BRAND_PERMISSIONS,
} from "@/lib/AuthEngine";

import Landing from "@/pages/Landing";

import RouteErrorBoundary from "@/components/RouteErrorBoundary";
import CustomerLogin from "@/pages/portal/CustomerLogin";
import CustomerRegister from "@/pages/portal/CustomerRegister";
// Iter-364 · Clean Removal. ForgotPassword + CustomerActivate pages
// were DELETED — the Guest Portal is passwordless.
import CustomerCheckIn from "@/pages/portal/CustomerCheckIn";
import BookFlow from "@/pages/portal/BookFlow";
import MyAppointments from "@/pages/portal/MyAppointments";
import CustomerProfile from "@/pages/portal/CustomerProfile";
import CustomerMedical from "@/pages/portal/CustomerMedical";
import CustomerMassagePreferences from "@/pages/portal/CustomerMassagePreferences";
import Concierge from "@/pages/portal/Concierge";
import LongevityDashboard from "@/pages/portal/LongevityDashboard";
import LongevityOnboarding from "@/pages/portal/LongevityOnboarding";
import BodyCompositionDetail from "@/pages/portal/BodyCompositionDetail";
import SkinAnalysisDetail from "@/pages/portal/SkinAnalysisDetail";
import SleepDetail from "@/pages/portal/SleepDetail";
import PrivacyLongevity from "@/pages/portal/PrivacyLongevity";

import StaffLogin from "@/pages/staff/StaffLogin";
import BranchPicker from "@/pages/staff/BranchPicker";
import BrandDashboard from "@/pages/staff/brand/BrandDashboard";
import BrandCategories from "@/pages/staff/brand/BrandCategories";
import BrandServices from "@/pages/staff/brand/BrandServices";
import BrandSeries from "@/pages/staff/brand/BrandSeries";
import BrandPackages from "@/pages/staff/brand/BrandPackages";
import BrandProducts from "@/pages/staff/brand/BrandProducts";
import BrandUsers from "@/pages/staff/brand/BrandUsers";
import BrandReports from "@/pages/staff/brand/BrandReports";
import BrandCancellationReasons from "@/pages/staff/brand/BrandCancellationReasons";
import BrandCancellationDashboard from "@/pages/staff/brand/BrandCancellationDashboard";
import BrandCancellationDetail from "@/pages/staff/brand/BrandCancellationDetail";
import BrandServicePerformanceDashboard from "@/pages/staff/brand/BrandServicePerformanceDashboard";
import BrandServicePerformanceDetail from "@/pages/staff/brand/BrandServicePerformanceDetail";
import BrandStaffUtilisationDashboard from "@/pages/staff/brand/BrandStaffUtilisationDashboard";
import BrandStaffUtilisationDetail from "@/pages/staff/brand/BrandStaffUtilisationDetail";

import BranchCalendar from "@/pages/staff/branch/BranchCalendar";
import MyRoster from "@/pages/staff/MyRoster";
import MyRosterDay from "@/pages/staff/MyRosterDay";
import MyRosterClient from "@/pages/staff/MyRosterClient";
import MyClients from "@/pages/staff/MyClients";
import MyPerformance from "@/pages/staff/MyPerformance";
import BrandCustomers from "@/pages/staff/brand/BrandCustomers";
import BrandCustomerDetail from "@/pages/staff/brand/BrandCustomerDetail";
import BrandCustomerHistory from "@/pages/staff/brand/BrandCustomerHistory";
import CustomerMerge from "@/pages/staff/brand/CustomerMerge";
import LaunchReset from "@/pages/staff/brand/LaunchReset";
import BranchOrders from "@/pages/staff/branch/BranchOrders";
import ApprovalsQueue from "@/pages/staff/branch/ApprovalsQueue";
import BranchInventory from "@/pages/staff/branch/BranchInventory";
import BranchReports from "@/pages/staff/branch/BranchReports";
import BranchOverview from "@/pages/staff/branch/BranchOverview";
import BranchOverviewPrint from "@/pages/staff/branch/BranchOverviewPrint";
import DailyDepositsReport from "@/pages/staff/branch/reports/DailyDepositsReport";
import OrdersReport from "@/pages/staff/branch/reports/OrdersReport";
import OrdersLegacyReport from "@/pages/staff/branch/reports/OrdersLegacyReport";
import OverrideReasonsReport from "@/pages/staff/branch/reports/OverrideReasonsReport";
import RefundsReport from "@/pages/staff/branch/reports/RefundsReport";
import UnpaidTransactionsReport from "@/pages/staff/branch/reports/UnpaidTransactionsReport";
import FinanceSummaryReport from "@/pages/staff/branch/reports/FinanceSummaryReport";
import CareTeamSalesReport from "@/pages/staff/branch/reports/CareTeamSalesReport";
import CareTeamProductivityReport from "@/pages/staff/branch/reports/CareTeamProductivityReport";
import CancellationReport from "@/pages/staff/branch/reports/CancellationReport";
import CustomersByDayReport from "@/pages/staff/branch/reports/CustomersByDayReport";
import TopSpendersReport from "@/pages/staff/branch/reports/TopSpendersReport";
import FutureBookingReport from "@/pages/staff/branch/reports/FutureBookingReport";
import ReferralReport from "@/pages/staff/branch/reports/ReferralReport";
import OverviewReport from "@/pages/staff/branch/reports/OverviewReport";
import EmailLogReport from "@/pages/staff/branch/reports/EmailLogReport";
import BranchSettings from "@/pages/staff/branch/BranchSettings";
import BranchSchedule from "@/pages/staff/branch/BranchSchedule";
import BranchScheduleEditor from "@/pages/staff/branch/BranchScheduleEditor";

import MarketingEmails from "@/pages/staff/branch/marketing/MarketingEmails";
import MarketingSpecials from "@/pages/staff/branch/marketing/MarketingSpecials";
import BrandGiftCertificates from "@/pages/staff/brand/BrandGiftCertificates";
import BrandLoyalty from "@/pages/staff/brand/BrandLoyalty";
import BrandBranches from "@/pages/staff/brand/BrandBranches";
import BrandNotifications from "@/pages/staff/brand/BrandNotifications";

function CustomerRoute({ children }) {
  const { customer, loading } = useAuth();
  if (loading) return <div className="p-10 font-editorial text-2xl">Loading…</div>;
  if (!customer) return <Navigate to="/portal/login" replace />;
  return children;
}

// Re-export the AuthEngine's brand-workspace helpers for any legacy
// call sites that still import them from `@/App`. Do NOT redefine — the
// engine is the single source of truth (Iter-302).
export { hasAnyBrandPermission, isPersonalViewOnly };
export { BRAND_PERMISSIONS };

function StaffRoute({ children, brandOnly = false, ownerOnly = false }) {
  const { staff, loading } = useAuth();
  if (loading) return <div className="p-10 text-sm">Loading…</div>;
  if (!staff) return <Navigate to="/staff/login" replace />;
  // Owner-only routes short-circuit BEFORE brand-permission checks —
  // this is the hardest gate on the app (Launch Reset, future
  // destructive tools) and even a Brand Admin must not be able to
  // reach it by typing the URL manually. Delegated to AuthEngine so
  // the same predicate powers the sidebar menu filter.
  if (ownerOnly && !canAccessLaunchReset(staff)) {
    return <Navigate to="/staff/branches" replace />;
  }
  // Brand workspace is reserved for the Owner or any role holding at
  // least one brand-level permission (roles, staff mgmt, catalog, reports).
  if (brandOnly && !hasAnyBrandPermission(staff)) {
    return <Navigate to="/staff/branches" replace />;
  }
  return children;
}

export default function App() {
  return (
    <AuthProvider>
      <TooltipProvider delayDuration={200}>
        <div className="App">
          <BrowserRouter>
          <KickedOutDialog />
          <Routes>
            <Route path="/" element={<Landing />} />

            {/* Customer portal */}
            <Route path="/portal/login" element={<RouteErrorBoundary><CustomerLogin /></RouteErrorBoundary>} />
            <Route path="/portal/register" element={<RouteErrorBoundary><CustomerRegister /></RouteErrorBoundary>} />
            <Route path="/portal/checkin" element={<RouteErrorBoundary><CustomerCheckIn /></RouteErrorBoundary>} />
            <Route path="/portal/book" element={<CustomerRoute><BookFlow /></CustomerRoute>} />
            <Route path="/portal/appointments" element={<CustomerRoute><MyAppointments /></CustomerRoute>} />
            <Route path="/portal/profile" element={<CustomerRoute><CustomerProfile /></CustomerRoute>} />
            <Route path="/portal/medical" element={<CustomerRoute><CustomerMedical /></CustomerRoute>} />
            <Route path="/portal/massage-preferences" element={<CustomerRoute><CustomerMassagePreferences /></CustomerRoute>} />
            <Route path="/portal/concierge" element={<CustomerRoute><Concierge /></CustomerRoute>} />
            <Route path="/portal/longevity" element={<CustomerRoute><LongevityDashboard /></CustomerRoute>} />
            <Route path="/portal/longevity/onboarding" element={<CustomerRoute><LongevityOnboarding /></CustomerRoute>} />
            <Route path="/portal/longevity/body-composition" element={<CustomerRoute><BodyCompositionDetail /></CustomerRoute>} />
            <Route path="/portal/longevity/skin-analysis" element={<CustomerRoute><SkinAnalysisDetail /></CustomerRoute>} />
            <Route path="/portal/longevity/recovery-sleep" element={<CustomerRoute><SleepDetail /></CustomerRoute>} />
            <Route path="/privacy-longevity" element={<RouteErrorBoundary><PrivacyLongevity /></RouteErrorBoundary>} />

            {/* Staff */}
            <Route path="/staff/login" element={<StaffLogin />} />
            <Route path="/staff/branches" element={<StaffRoute><BranchPicker /></StaffRoute>} />
            <Route path="/staff/my-roster" element={<StaffRoute><MyRoster /></StaffRoute>} />
            <Route path="/staff/my-roster/day" element={<StaffRoute><MyRosterDay /></StaffRoute>} />
            <Route path="/staff/my-roster/client" element={<StaffRoute><MyRosterClient /></StaffRoute>} />
            <Route path="/staff/my-clients" element={<StaffRoute><MyClients /></StaffRoute>} />
            <Route path="/staff/my-performance" element={<StaffRoute><MyPerformance /></StaffRoute>} />

            {/* Brand */}
            <Route path="/staff/brand" element={<StaffRoute brandOnly><BrandDashboard /></StaffRoute>} />
            <Route path="/staff/brand/categories" element={<StaffRoute brandOnly><BrandCategories /></StaffRoute>} />
            <Route path="/staff/brand/services" element={<StaffRoute brandOnly><BrandServices /></StaffRoute>} />
            <Route path="/staff/brand/series" element={<StaffRoute brandOnly><BrandSeries /></StaffRoute>} />
            <Route path="/staff/brand/packages" element={<StaffRoute brandOnly><BrandPackages /></StaffRoute>} />
            <Route path="/staff/brand/products" element={<StaffRoute brandOnly><BrandProducts /></StaffRoute>} />
            <Route path="/staff/brand/users" element={<StaffRoute brandOnly><BrandUsers /></StaffRoute>} />
            <Route path="/staff/brand/reports" element={<StaffRoute brandOnly><BrandReports /></StaffRoute>} />
            {/* Iter-409 — Brand-level Cancellation dashboard (Overview sub-menu). */}
            <Route path="/staff/brand/cancellations" element={<StaffRoute brandOnly><BrandCancellationDashboard /></StaffRoute>} />
            <Route path="/staff/brand/cancellations/:bid" element={<StaffRoute brandOnly><BrandCancellationDetail /></StaffRoute>} />
            <Route path="/staff/brand/service-performance" element={<StaffRoute brandOnly><BrandServicePerformanceDashboard /></StaffRoute>} />
            <Route path="/staff/brand/service-performance/:bid" element={<StaffRoute brandOnly><BrandServicePerformanceDetail /></StaffRoute>} />
            {/* Iter-f6 — Brand-level Staff Utilisation dashboard (Overview sub-menu). */}
            <Route path="/staff/brand/staff-utilisation" element={<StaffRoute brandOnly><BrandStaffUtilisationDashboard /></StaffRoute>} />
            <Route path="/staff/brand/staff-utilisation/:bid" element={<StaffRoute brandOnly><BrandStaffUtilisationDetail /></StaffRoute>} />
            <Route path="/staff/brand/customers" element={<StaffRoute brandOnly><BrandCustomers /></StaffRoute>} />
            <Route path="/staff/brand/customers/merge" element={<StaffRoute brandOnly><CustomerMerge /></StaffRoute>} />
            <Route path="/staff/brand/customers/:cid" element={<StaffRoute brandOnly><BrandCustomerDetail /></StaffRoute>} />
            <Route path="/staff/brand/customers/:cid/appointments" element={<StaffRoute brandOnly><BrandCustomerHistory kind="appointments" /></StaffRoute>} />
            <Route path="/staff/brand/customers/:cid/orders" element={<StaffRoute brandOnly><BrandCustomerHistory kind="orders" /></StaffRoute>} />
            <Route path="/staff/brand/general/cancellation-reasons" element={<StaffRoute brandOnly><BrandCancellationReasons /></StaffRoute>} />
            <Route path="/staff/brand/general/launch-reset" element={<StaffRoute brandOnly ownerOnly><LaunchReset /></StaffRoute>} />
            <Route path="/staff/brand/gift-certificates" element={<StaffRoute brandOnly><BrandGiftCertificates /></StaffRoute>} />
            <Route path="/staff/brand/loyalty" element={<StaffRoute brandOnly><BrandLoyalty /></StaffRoute>} />
            <Route path="/staff/brand/branches" element={<StaffRoute brandOnly ownerOnly><BrandBranches /></StaffRoute>} />
            <Route path="/staff/brand/notifications" element={<StaffRoute brandOnly ownerOnly><BrandNotifications /></StaffRoute>} />
            <Route path="/staff/brand/roles" element={<Navigate to="/staff/brand/users?tab=roles" replace />} />

            {/* Branch workspace */}
            <Route path="/staff/branch/:bid" element={<StaffRoute><BranchCalendar /></StaffRoute>} />
            <Route path="/staff/branch/:bid/calendar" element={<StaffRoute><BranchCalendar /></StaffRoute>} />
            {/* Customers moved to brand-level (single source of truth). Redirect legacy per-branch URL. */}
            <Route path="/staff/branch/:bid/customers" element={<Navigate to="/staff/brand/customers" replace />} />
            <Route path="/staff/branch/:bid/orders" element={<StaffRoute><BranchOrders /></StaffRoute>} />
            <Route path="/staff/branch/:bid/approvals" element={<StaffRoute><ApprovalsQueue /></StaffRoute>} />
            <Route path="/staff/branch/:bid/inventory" element={<StaffRoute><BranchInventory /></StaffRoute>} />
            <Route path="/staff/branch/:bid/reports" element={<StaffRoute><BranchReports /></StaffRoute>} />
            <Route path="/staff/branch/:bid/overview" element={<StaffRoute><BranchOverview /></StaffRoute>} />
            {/* Print-mode Branch Overview — used by the Automated
                Notifications snapshot pipeline. Auth is via a short-
                lived snapshot token in the URL query, NOT the staff
                JWT, so intentionally NOT wrapped in <StaffRoute>. */}
            <Route path="/staff/branch/:bid/print/overview" element={<BranchOverviewPrint />} />
            <Route path="/staff/branch/:bid/reports/daily-deposits" element={<StaffRoute><DailyDepositsReport /></StaffRoute>} />
            <Route path="/staff/branch/:bid/reports/orders" element={<StaffRoute><OrdersReport /></StaffRoute>} />
            <Route path="/staff/branch/:bid/reports/orders-legacy" element={<StaffRoute><OrdersLegacyReport /></StaffRoute>} />
            <Route path="/staff/branch/:bid/reports/override-reasons" element={<StaffRoute><OverrideReasonsReport /></StaffRoute>} />
            <Route path="/staff/branch/:bid/reports/refunds" element={<StaffRoute><RefundsReport /></StaffRoute>} />
            <Route path="/staff/branch/:bid/reports/unpaid-transactions" element={<StaffRoute><UnpaidTransactionsReport /></StaffRoute>} />
            <Route path="/staff/branch/:bid/reports/finance-summary" element={<StaffRoute><FinanceSummaryReport /></StaffRoute>} />
            <Route path="/staff/branch/:bid/reports/care-team-sales" element={<StaffRoute><CareTeamSalesReport /></StaffRoute>} />
            <Route path="/staff/branch/:bid/reports/care-team-productivity" element={<StaffRoute><CareTeamProductivityReport /></StaffRoute>} />
            <Route path="/staff/branch/:bid/reports/cancellations" element={<StaffRoute><CancellationReport /></StaffRoute>} />
            <Route path="/staff/branch/:bid/reports/customers-by-day" element={<StaffRoute><CustomersByDayReport /></StaffRoute>} />
            <Route path="/staff/branch/:bid/reports/top-spenders" element={<StaffRoute><TopSpendersReport /></StaffRoute>} />
            <Route path="/staff/branch/:bid/reports/future-bookings" element={<StaffRoute><FutureBookingReport /></StaffRoute>} />
            <Route path="/staff/branch/:bid/reports/referrals" element={<StaffRoute><ReferralReport /></StaffRoute>} />
            <Route path="/staff/branch/:bid/reports/overview" element={<StaffRoute><OverviewReport /></StaffRoute>} />
            <Route path="/staff/branch/:bid/reports/email-log" element={<StaffRoute><EmailLogReport /></StaffRoute>} />
            <Route path="/staff/branch/:bid/schedule" element={<StaffRoute><BranchSchedule /></StaffRoute>} />
            <Route path="/staff/branch/:bid/schedule/:uid" element={<StaffRoute><BranchScheduleEditor /></StaffRoute>} />
            <Route path="/staff/branch/:bid/settings" element={<StaffRoute><BranchSettings /></StaffRoute>} />

            {/* Marketing (per-location) */}
            <Route path="/staff/branch/:bid/marketing/emails" element={<StaffRoute><MarketingEmails /></StaffRoute>} />
            <Route path="/staff/branch/:bid/marketing/specials" element={<StaffRoute><MarketingSpecials /></StaffRoute>} />
            <Route path="/staff/branch/:bid/marketing/loyalty" element={<Navigate to="/staff/brand/loyalty" replace />} />
            {/* Gift Certificates were promoted to a brand-level template so
                they can apply to every branch. Old per-branch URL redirects
                to the new brand page. */}
            <Route path="/staff/branch/:bid/marketing/gift-cards" element={<Navigate to="/staff/brand/gift-certificates" replace />} />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </BrowserRouter>
          <Toaster position="top-right" richColors />
        </div>
      </TooltipProvider>
    </AuthProvider>
  );
}
